import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { paymentsApi } from '../utils/paymentsStore';
import { STATUS_LABELS } from '../utils/claimMath';
import { PageHeader, Panel, StatusPill, Icon, useToast, fmtMoney, fmtDate, Pager, usePager } from '../components/ui';

// Target fields and the spreadsheet headers that map to them automatically.
const FIELDS = [
  { key: 'dosStart', label: 'Date of service start', required: true, aliases: ['date of service start', 'dos start', 'service start', 'start date', 'from'] },
  { key: 'dosEnd', label: 'Date of service end', aliases: ['date of service end', 'dos end', 'service end', 'end date', 'to', 'through'] },
  { key: 'datePaid', label: 'Date paid', aliases: ['date paid', 'paid date', 'payment date', 'check date'] },
  { key: 'payerName', label: 'Insurance company', required: true, aliases: ['insurance company', 'insurance', 'payer', 'plan', 'carrier'] },
  { key: 'patientName', label: 'Patient name', required: true, aliases: ['patient name', 'patient', 'client', 'client name', 'member name', 'name'] },
  { key: 'patientId', label: 'Patient ID', aliases: ['patient id', 'member id', 'medicaid id', 'patient name/id', 'id'] },
  { key: 'claimNumber', label: 'Claim #', aliases: ['claim #', 'claim number', 'claim no', 'claim', 'claim id', 'icn'] },
  { key: 'serviceType', label: 'Service type', aliases: ['service type', 'service', 'procedure', 'hcpcs'] },
  { key: 'unitsBilled', label: 'Units billed', aliases: ['units billed', 'billed units', 'units'] },
  { key: 'unitsPaid', label: 'Units paid', aliases: ['units paid', 'paid units'] },
  { key: 'amountBilled', label: 'Amount billed', required: true, aliases: ['amount billed', 'billed amount', 'billed', 'charges', 'charge amount', 'total billed'] },
  { key: 'amountPaid', label: 'Amount paid', aliases: ['amount paid', 'paid amount', 'paid', 'payment', 'payment amount', 'total paid'] },
  { key: 'status', label: 'Payment status', aliases: ['payment status', 'status', 'claim status'] },
  { key: 'statusNote', label: 'Note', aliases: ['note', 'notes', 'comment', 'comments', 'reason', 'denial reason'] },
];
const IGNORED_HEADERS = ['hours of service paid', 'adjustment', 'month', 'payment lag days', 'payment lag', 'lag days', 'hours paid'];

const norm = s => String(s || '').toLowerCase().replace(/[^a-z0-9#/]+/g, ' ').trim();

function autoMap(headers) {
  const map = {};
  const used = new Set();
  for (const f of FIELDS) {
    const hit = headers.find(h => !used.has(h) && f.aliases.includes(norm(h)));
    if (hit) { map[f.key] = hit; used.add(hit); }
  }
  for (const f of FIELDS) {
    if (map[f.key]) continue;
    const hit = headers.find(h => !used.has(h) && f.aliases.some(a => norm(h).includes(a)));
    if (hit) { map[f.key] = hit; used.add(hit); }
  }
  return map;
}

export default function PaymentsImportPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [showToast, toastNode] = useToast();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [sheet, setSheet] = useState('');
  const [workbook, setWorkbook] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [map, setMap] = useState({});
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState('update');
  const previewPager = usePager(preview ? preview.rows : [], [preview]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [over, setOver] = useState(false);
  const [result, setResult] = useState(null);


  const readFile = async (f) => {
    setError(''); setFile(f);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      setWorkbook(wb); setSheets(wb.SheetNames);
      const guess = wb.SheetNames.find(n => /payment|log|claims/i.test(n)) || wb.SheetNames[0];
      pickSheet(wb, guess);
    } catch (e) { setError('Could not read that file. Save it as .xlsx or .csv and try again.'); }
  };

  const pickSheet = (wb, name) => {
    setSheet(name);
    const ws = wb.Sheets[name];
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' });
    // Find the header row: the first row with at least 4 non-empty text cells.
    let hi = aoa.findIndex(r => r.filter(c => typeof c === 'string' && c.trim()).length >= 4);
    if (hi < 0) hi = 0;
    const hdr = (aoa[hi] || []).map((h, i) => (String(h).trim() || `Column ${i + 1}`));
    const body = aoa.slice(hi + 1).filter(r => r.some(c => c !== '' && c != null)).map(r => Object.fromEntries(hdr.map((h, i) => [h, r[i] instanceof Date ? r[i].toISOString().slice(0, 10) : r[i]])));
    setHeaders(hdr); setRows(body); setMap(autoMap(hdr));
  };

  const onDrop = (e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0]; if (f) readFile(f); };

  const unmappedRequired = FIELDS.filter(f => f.required && !map[f.key]);
  const unusedHeaders = headers.filter(h => !Object.values(map).includes(h) && !IGNORED_HEADERS.includes(norm(h)));

  const mapped = useMemo(() => rows.map(r => Object.fromEntries(FIELDS.filter(f => map[f.key]).map(f => [f.key, r[map[f.key]]]))), [rows, map]);

  const runPreview = async () => {
    setBusy(true); setError('');
    try { setPreview(await paymentsApi.importPreview(mapped)); setStep(3); }
    catch (e) { setError(e.message || 'Preview failed'); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const r = await paymentsApi.importCommit(mapped, { mode, fileName: file?.name || '' });
      setResult(r); showToast(`Imported ${r.inserted} new and ${r.updated} updated claims`);
    } catch (e) { setError(e.message || 'Import failed'); }
    finally { setBusy(false); }
  };

  return (
    <main className="main-content ops-main">
      <div className="ops-page">
        <PageHeader title="Import payments" subtitle="Bring in the Insurance Payment Tracker workbook or a CSV export. Reviewed rows are saved to the shared payment log that every administrator sees."
          actions={<Link to="/payments" className="ops-btn">Back to payments</Link>} />

        <ol className="ops-stepper">
          {['Upload file', 'Map columns', 'Review and import'].map((l, i) => (
            <React.Fragment key={l}>
              {i > 0 && <li className="ops-step-line" aria-hidden="true" />}
              <li className={`ops-step${step === i + 1 ? ' is-active' : step > i + 1 ? ' is-done' : ''}`}><span>{step > i + 1 ? <Icon name="check" size={14} /> : i + 1}</span>{l}</li>
            </React.Fragment>
          ))}
        </ol>

        {error && <div className="ops-error">{error}</div>}

        {step === 1 && (
          <Panel title="Choose a file">
            <div className={`ops-dropzone${over ? ' is-over' : ''}`} onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}
              onClick={() => fileRef.current?.click()} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && fileRef.current?.click()}>
              <Icon name="upload" size={28} />
              <div><b>Drop the workbook here</b> or click to browse</div>
              <div className="ops-cell-sub">.xlsx, .xls or .csv. Use the Payment Log sheet; the dashboard sheet is recalculated here.</div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={e => e.target.files?.[0] && readFile(e.target.files[0])} />
            </div>
            {file && (
              <div className="ops-form-grid" style={{ marginTop: 16 }}>
                <div className="ops-field"><span className="ops-label">File</span><div className="ops-readout">{file.name}</div></div>
                <label className="ops-field">Sheet
                  <select className="ops-select" value={sheet} onChange={e => pickSheet(workbook, e.target.value)}>{sheets.map(s => <option key={s}>{s}</option>)}</select>
                </label>
                <div className="ops-field"><span className="ops-label">Rows found</span><div className="ops-readout">{rows.length}</div></div>
                <div className="ops-field"><span className="ops-label">Columns</span><div className="ops-readout">{headers.length}</div></div>
              </div>
            )}
            <div className="ops-actions" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="ops-btn ops-btn--primary" disabled={!rows.length} onClick={() => setStep(2)}>Continue to mapping</button>
            </div>
          </Panel>
        )}

        {step === 2 && (
          <div className="ops-split">
            <Panel title="Map columns" count={`${Object.keys(map).length} of ${FIELDS.length}`}>
              <p className="ops-help">Columns were matched to the Payment Log headers automatically. Fix any that are wrong. Hours, adjustment, month and lag days are recalculated, so they are not imported.</p>
              <div className="ops-map-grid">
                {FIELDS.map(f => (
                  <div key={f.key} className="ops-map-row">
                    <div><b>{f.label}</b>{f.required && <span className="neg"> *</span>}</div>
                    <select className="ops-select" value={map[f.key] || ''} onChange={e => setMap(m => { const n = { ...m }; if (e.target.value) n[f.key] = e.target.value; else delete n[f.key]; return n; })}>
                      <option value="">Not imported</option>
                      {headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              {unmappedRequired.length > 0 && <div className="ops-error" style={{ marginTop: 12 }}>Required: {unmappedRequired.map(f => f.label).join(', ')}</div>}
              {unusedHeaders.length > 0 && <div className="ops-help" style={{ marginTop: 12 }}>Not used: {unusedHeaders.join(', ')}</div>}
              <div className="ops-actions" style={{ justifyContent: 'space-between', marginTop: 16 }}>
                <button type="button" className="ops-btn" onClick={() => setStep(1)}>Back</button>
                <button type="button" className="ops-btn ops-btn--primary" disabled={unmappedRequired.length > 0 || busy} onClick={runPreview}>{busy ? 'Checking…' : 'Preview import'}</button>
              </div>
            </Panel>
            <Panel title="First rows as they will import" flush>
              <div className="ops-table-wrap">
                <table className="ops-table ops-table--dense">
                  <thead><tr>{FIELDS.filter(f => map[f.key]).map(f => <th key={f.key}>{f.label}</th>)}</tr></thead>
                  <tbody>{mapped.slice(0, 8).map((r, i) => <tr key={i}>{FIELDS.filter(f => map[f.key]).map(f => <td key={f.key}>{String(r[f.key] ?? '')}</td>)}</tr>)}</tbody>
                </table>
              </div>
            </Panel>
          </div>
        )}

        {step === 3 && preview && !result && (
          <>
            <div className="ops-kpis">
              <div className="ops-kpi"><span className="ops-kpi__label">Rows</span><span className="ops-kpi__value">{preview.counts.total}</span></div>
              <div className="ops-kpi ops-kpi--green"><span className="ops-kpi__label">New claims</span><span className="ops-kpi__value">{preview.counts.newRows}</span></div>
              <div className="ops-kpi ops-kpi--blue"><span className="ops-kpi__label">Already here</span><span className="ops-kpi__value">{preview.counts.updates}</span><span className="ops-kpi__sub">{mode === 'update' ? 'will be updated' : 'will be skipped'}</span></div>
              <div className={`ops-kpi${preview.counts.invalid ? ' ops-kpi--red' : ''}`}><span className="ops-kpi__label">Invalid</span><span className="ops-kpi__value">{preview.counts.invalid}</span><span className="ops-kpi__sub">skipped</span></div>
              <div className={`ops-kpi${preview.counts.unmatchedPayers ? ' ops-kpi--amber' : ''}`}><span className="ops-kpi__label">Unknown payers</span><span className="ops-kpi__value">{preview.counts.unmatchedPayers}</span><span className="ops-kpi__sub"><Link to="/payments/payers" className="ops-link">Add aliases</Link></span></div>
              <div className={`ops-kpi${preview.counts.unmatchedClients ? ' ops-kpi--amber' : ''}`}><span className="ops-kpi__label">Unlinked patients</span><span className="ops-kpi__value">{preview.counts.unmatchedClients}</span><span className="ops-kpi__sub">no client record matched</span></div>
            </div>
            <Panel title="Review" flush
              action={<label className="ops-field--inline">Existing claims
                <select className="ops-select" value={mode} onChange={e => setMode(e.target.value)}><option value="update">Update with file values</option><option value="skip">Leave unchanged</option></select>
              </label>}
              foot={<div className="ops-actions" style={{ justifyContent: 'space-between' }}>
                <button type="button" className="ops-btn" onClick={() => setStep(2)}>Back to mapping</button>
                <button type="button" className="ops-btn ops-btn--primary" disabled={busy || preview.counts.valid === 0} onClick={commit}>{busy ? 'Importing…' : `Import ${preview.counts.newRows + (mode === 'update' ? preview.counts.updates : 0)} claims`}</button>
              </div>}>
              <div className="ops-table-wrap" style={{ maxHeight: 480 }}>
                <table className="ops-table ops-table--dense">
                  <thead><tr><th>Row</th><th>Result</th><th>Patient</th><th>Payer</th><th>Claim #</th><th>Service</th><th className="num">Billed</th><th className="num">Paid</th><th>Status</th></tr></thead>
                  <tbody>
                    {previewPager.pageRows.map(r => (
                      <tr key={r.row}>
                        <td className="mono">{r.row}</td>
                        <td>{r.errors.length ? <span className="ops-pill ops-pill--red"><Icon name="x" size={12} />{r.errors.join('; ')}</span> : r.dupInFile ? <span className="ops-pill ops-pill--amber">Duplicate in file</span> : r.exists ? <span className="ops-pill ops-pill--blue">{mode === 'update' ? 'Update' : 'Skip'}</span> : <span className="ops-pill ops-pill--green">New</span>}</td>
                        <td><div className="ops-cell-main">{r.claim.patientName}</div><div className={`ops-cell-sub${r.clientMatched ? '' : ' neg'}`}>{r.clientMatched ? 'linked to client' : 'no client match'}</div></td>
                        <td><div>{r.claim.payerName}</div>{!r.payerMatched && <div className="ops-cell-sub neg">unknown payer</div>}</td>
                        <td className="mono">{r.claim.claimNumber}</td>
                        <td>{fmtDate(r.claim.dosStart)}{r.claim.dosEnd && r.claim.dosEnd !== r.claim.dosStart ? ` to ${fmtDate(r.claim.dosEnd)}` : ''}</td>
                        <td className="num">{fmtMoney(r.claim.amountBilledCents)}</td>
                        <td className="num">{fmtMoney(r.claim.amountPaidCents)}</td>
                        <td><StatusPill status={r.claim.status} label={STATUS_LABELS[r.claim.status]} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="ops-panel__foot"><Pager {...previewPager.pagerProps} label="rows" /></div>
            </Panel>
          </>
        )}

        {result && (
          <Panel title="Import complete">
            <div className="ops-stat-row">
              <div className="ops-stat"><b>{result.inserted}</b><span>New claims</span></div>
              <div className="ops-stat"><b>{result.updated}</b><span>Updated</span></div>
              <div className="ops-stat"><b>{result.skipped}</b><span>Skipped</span></div>
            </div>
            <p className="ops-help">Batch {result.batchId}. You can roll this batch back from Settings if the numbers look wrong.</p>
            <div className="ops-actions">
              <button type="button" className="ops-btn ops-btn--primary" onClick={() => navigate('/payments')}>Open payment log</button>
              <button type="button" className="ops-btn" onClick={() => navigate('/dashboard')}>Go to dashboard</button>
            </div>
          </Panel>
        )}
      </div>
      {toastNode}
    </main>
  );
}
